import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Eye, Wand2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Select } from '../components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Badge } from '../components/ui/badge'
import { formatDate } from '../lib/utils'
import { Post, Category } from '../types'
import { api } from '../services/api'
import { authService } from '../services/auth'
import AdminLogin from '../components/AdminLogin'

const AdminDashboard: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(true)
  const [aiLoading, setAiLoading] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // Add meta tag to prevent indexing
  useEffect(() => {
    const metaTag = document.createElement('meta')
    metaTag.name = 'robots'
    metaTag.content = 'noindex, nofollow'
    document.head.appendChild(metaTag)

    return () => {
      document.head.removeChild(metaTag)
    }
  }, [])

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category_id: '',
    status: 'draft' as 'draft' | 'published'
  })

  // AI form state
  const [aiFormData, setAiFormData] = useState({
    contentType: 'Excel Tutorial',
    topic: '',
    targetAudience: 'Beginner',
    includeExamples: true
  })

  useEffect(() => {
    checkAuthentication()
  }, [])

  useEffect(() => {
    if (authenticated) {
      fetchData()
    }
  }, [authenticated])

  const checkAuthentication = async () => {
    try {
      const isAuth = await authService.checkSession()
      setAuthenticated(isAuth)
    } catch (error) {
      console.error('Auth check failed:', error)
      setAuthenticated(false)
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogin = async (email: string, password: string) => {
    try {
      setLoginLoading(true)
      setLoginError('')
      await authService.login(email, password)
      setAuthenticated(true)
    } catch (error: any) {
      console.error('Login failed:', error)
      setLoginError(error.response?.data?.error || 'Login failed')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await authService.logout()
      setAuthenticated(false)
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const fetchData = async () => {
    try {
      const [postsRes, categoriesRes] = await Promise.all([
        api.get('/posts'),
        api.get('/categories')
      ])
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : [])
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingPost) {
        await api.put(`/posts/${editingPost.id}`, formData)
      } else {
        await api.post('/posts', formData)
      }
      
      await fetchData()
      resetForm()
      setIsCreateDialogOpen(false)
    } catch (error) {
      console.error('Error saving post:', error)
    }
  }

  const handleAIGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    setAiLoading(true)

    try {
      const response = await api.post('/ai/generate-content', aiFormData)
      const aiContent = response.data

      setFormData({
        title: aiContent.title,
        slug: aiContent.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, ''),
        content: aiContent.content,
        excerpt: aiContent.excerpt,
        category_id: categories.find(cat => 
          cat.name.toLowerCase().includes(aiFormData.contentType.toLowerCase().split(' ')[0])
        )?.id || '',
        status: 'draft'
      })

      setIsAIDialogOpen(false)
      setIsCreateDialogOpen(true)
    } catch (error) {
      console.error('Error generating AI content:', error)
    } finally {
      setAiLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return

    try {
      await api.delete(`/posts/${id}`)
      await fetchData()
    } catch (error) {
      console.error('Error deleting post:', error)
    }
  }

  const handleEdit = (post: Post) => {
    setEditingPost(post)
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      category_id: post.category_id,
      status: post.status
    })
    setIsCreateDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      category_id: '',
      status: 'draft'
    })
    setEditingPost(null)
  }

  const getCategoryName = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)?.name || 'Unknown'
  }

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId)
    const colorMap: Record<string, string> = {
      'excel-tutorials': 'excel',
      'tips-tricks': 'amber', 
      'news-updates': 'blue',
      'advanced-techniques': 'purple',
      'business-solutions': 'teal',
      'excel-terms': 'purple',
      'excel': 'excel'
    }
    return colorMap[category?.slug || ''] || 'default'
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (!authenticated) {
    return (
      <AdminLogin 
        onLogin={handleLogin}
        error={loginError}
        loading={loginLoading}
      />
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage your Excel content and blog posts</p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button 
              variant="outline" 
              onClick={handleLogout}
            >
              Logout
            </Button>
            <Button 
              variant="premium" 
              onClick={() => setIsAIDialogOpen(true)}
            >
              <Wand2 className="h-4 w-4 mr-2" />
              AI Generate
            </Button>
            <Button 
              variant="excel" 
              onClick={() => {
                resetForm()
                setIsCreateDialogOpen(true)
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Posts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{posts.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Published</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-excel-green">
                {posts.filter(p => p.status === 'published').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Drafts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-excel-amber">
                {posts.filter(p => p.status === 'draft').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Views</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-excel-blue">
                {posts.reduce((sum, post) => sum + post.view_count, 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Posts List */}
        <Card>
          <CardHeader>
            <CardTitle>Posts</CardTitle>
            <CardDescription>Manage your blog posts and articles</CardDescription>
          </CardHeader>
          <CardContent>
            {posts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No posts yet. Create your first post!</p>
                <Button variant="excel" onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={getCategoryColor(post.category_id) as any}>
                          {getCategoryName(post.category_id)}
                        </Badge>
                        <Badge variant={post.status === 'published' ? 'excel' : 'amber'}>
                          {post.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{post.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatDate(post.created_at)}</span>
                        <span className="flex items-center">
                          <Eye className="h-3 w-3 mr-1" />
                          {post.view_count}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {post.status === 'published' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const category = categories.find(cat => cat.id === post.category_id);
                            if (category) {
                              window.open(`/${category.slug}/${post.slug}`, '_blank');
                            }
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(post)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(post.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Post Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Slug</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({...formData, slug: e.target.value})}
                placeholder="article-url-slug"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                required
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Excerpt</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={12}
                className="font-mono"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value as 'draft' | 'published'})}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="excel">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog open={isAIDialogOpen} onOpenChange={setIsAIDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>AI Content Generator</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAIGenerate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Content Type</label>
              <Select
                value={aiFormData.contentType}
                onChange={(e) => setAiFormData({...aiFormData, contentType: e.target.value})}
              >
                <option value="Excel Tutorial">Excel Tutorial</option>
                <option value="Tips & Tricks">Tips & Tricks</option>
                <option value="News Update">News Update</option>
                <option value="Advanced Technique">Advanced Technique</option>
                <option value="Business Solution">Business Solution</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Topic</label>
              <Input
                value={aiFormData.topic}
                onChange={(e) => setAiFormData({...aiFormData, topic: e.target.value})}
                placeholder="e.g., VLOOKUP formulas, Pivot Tables, Excel shortcuts"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Target Audience</label>
              <Select
                value={aiFormData.targetAudience}
                onChange={(e) => setAiFormData({...aiFormData, targetAudience: e.target.value})}
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Professional">Professional</option>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeExamples"
                checked={aiFormData.includeExamples}
                onChange={(e) => setAiFormData({...aiFormData, includeExamples: e.target.checked})}
              />
              <label htmlFor="includeExamples" className="text-sm">
                Include practical examples
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsAIDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="premium" disabled={aiLoading}>
                {aiLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate Content
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AdminDashboard
